import {
  FormEvent, useCallback, useEffect, useState,
} from 'react';
import { cn } from '@/util/cn';
import { useOTSTranslation } from '@/i18n/useOTSTranslation';
import { SearchPopup } from '@/components/home/SearchPopup';
import { useAnalytics } from '@/tracking/analytics';
import { hasSoftKeyboard, primeSoftKeyboard } from '@/util/softKeyboard';

const formClass = [
  'relative mx-auto mb-[50px] w-3/5',
  'max-xl:w-full',
].join(' ');

const inputClass = [
  'block w-full h-[60px] bg-white opacity-[0.85]',
  'shadow-[0_15px_53px_rgba(0,0,0,0.23)] rounded-[9px]',
  'text-[1.009375rem] leading-[117%] text-[#576A85]',
  'px-[18px] py-5 border-0 outline-none',
  'max-md:h-[45px] max-md:rounded-[9px] max-md:text-xs',
  'max-md:text-[#1F1F1F] max-md:px-[13px] max-md:py-[14px] max-md:opacity-100',
].join(' ');

const btnClass = [
  'absolute top-[5px] right-[5px] min-w-[120px] h-[50px]',
  'bg-[#33B0D9] rounded-md',
  'font-medium text-sm leading-[117%] text-white border-0',
  'max-md:min-w-[86px] max-md:h-[34px]',
].join(' ');

export const Search = ({
  className,
  placeholder,
  searchQuery,
  clearSearchQuery,
}: {
  className?: string,
  placeholder?: string,
  searchQuery?: string | string[] | undefined,
  clearSearchQuery?: () => void,
}) => {
  const { t } = useOTSTranslation('common');
  const a = useAnalytics();
  const [isSearchOpen, setSearchOpen] = useState<boolean>(false);

  const toggleSearch = useCallback(() => {
    if (clearSearchQuery) {
      clearSearchQuery();
    }
    const willOpen = !isSearchOpen;
    if (willOpen) {
      // Inside the tap, so the on-screen keyboard is already up when
      // SearchPopup mounts and its input takes focus over.
      primeSoftKeyboard();
      a.searchBox();
    }
    setSearchOpen(willOpen);
    // `a` (useAnalytics) is a fresh object every render, so it stays out
    // of the deps: a churning toggleSearch identity re-runs SearchPopup's
    // close-on-route-change effect and snaps the overlay shut.
  }, [isSearchOpen]);

  useEffect(() => {
    setSearchOpen(!!searchQuery);
  }, [searchQuery]);

  const handleSubmit = useCallback((e:FormEvent<HTMLFormElement> | undefined) => e?.preventDefault(), []);

  // This field is a decoy — tapping it opens the full-screen popup, and
  // blurring keeps anyone from typing into the copy that stays behind.
  // On touch devices the blur would also dismiss the keyboard the tap
  // just raised, a beat before the popup asks for it again; leave focus
  // alone there and let the handover in SearchPopup do the work.
  const handleFocusSearch = useCallback((e: React.FocusEvent<HTMLInputElement> | undefined) => {
    if (!hasSoftKeyboard()) e?.target.blur();
  }, []);

  return (
    <>
      {isSearchOpen && (
        <SearchPopup
          closePopup={toggleSearch}
          query={searchQuery}
        />
      )}
      <form
        className={cn(formClass, className)}
        onSubmit={handleSubmit}
      >
        <input
          className={inputClass}
          type="text"
          placeholder={!placeholder ? t('homePage.searchBarHelpText') : placeholder}
          onClick={toggleSearch}
          onFocus={handleFocusSearch}
        />
        <button
          type="submit"
          className={btnClass}
          onClick={toggleSearch}
        >
          {t('common.searchButton')}
        </button>
      </form>
    </>
  );
};
